import { useEffect, useMemo } from 'react';
import { useSimulationStore } from '../../../store/simulationStore';
import { useVersionStore, type SavedVersion } from '../../../store/versionStore';

interface VersionHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onRestore: (version: SavedVersion) => void;
}

const formatTimestamp = (iso: string) => {
  const date = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const formatRelative = (iso: string) => {
  const createdAt = new Date(iso).getTime();
  const diff = Date.now() - createdAt;

  const minute = 1000 * 60;
  const hour = minute * 60;
  const day = hour * 24;

  if (diff < minute) {
    return 'Just now';
  }
  if (diff < hour) {
    const minutes = Math.round(diff / minute);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  if (diff < day) {
    const hours = Math.round(diff / hour);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  const days = Math.round(diff / day);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const VersionHistoryPanel = ({ isOpen, onClose, onRestore }: VersionHistoryPanelProps) => {
  const currentSimulationId = useSimulationStore((state) => state.currentSimulationId);
  const currentVersionId = useSimulationStore((state) => state.currentVersionId);
  const fetchVersions = useVersionStore((state) => state.fetchVersions);
  const versions = useVersionStore((state) => state.versions);
  const loading = useVersionStore((state) => state.loading);

  useEffect(() => {
    if (!isOpen || !currentSimulationId) {
      return;
    }

    fetchVersions(currentSimulationId);
  }, [isOpen, currentSimulationId, fetchVersions]);

  const simulationVersions = useMemo(() => {
    if (!currentSimulationId) {
      return [] as SavedVersion[];
    }

    return versions
      .filter((version) => version.simulation_id === currentSimulationId)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [versions, currentSimulationId]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="version-history-overlay" onClick={onClose}>
      <aside className="version-history-panel" onClick={(event) => event.stopPropagation()}>
        <div className="version-history-header">
          <h2>Version history</h2>
          <button
            className="version-history-close"
            onClick={onClose}
            aria-label="Close version history"
          >
            ×
          </button>
        </div>

        <div className="version-history-content">
          {loading && simulationVersions.length === 0 ? (
            <div className="version-history-empty">Loading versions...</div>
          ) : simulationVersions.length === 0 ? (
            <div className="version-history-empty">
              <p>No versions yet</p>
              <p className="version-history-empty-hint">
                Edits are auto-saved and will appear here.
              </p>
            </div>
          ) : (
            simulationVersions.map((version) => {
              const isCurrent = version.id === currentVersionId;
              return (
                <div
                  key={version.id}
                  className={`version-history-item ${isCurrent ? 'current' : ''}`}
                >
                  <div className="version-history-item-main">
                    <div className="version-history-item-title">{version.name}</div>
                    <div className="version-history-item-meta">
                      <span>{formatTimestamp(version.created_at)}</span>
                      <span className="version-history-dot">•</span>
                      <span>{formatRelative(version.created_at)}</span>
                    </div>
                    {version.description && (
                      <p className="version-history-item-description">{version.description}</p>
                    )}
                  </div>
                  <div className="version-history-item-actions">
                    {isCurrent ? (
                      <span className="version-history-current-label">Current</span>
                    ) : (
                      <button
                        className="btn btn-secondary"
                        onClick={() => onRestore(version)}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>
    </div>
  );
};

